import { HttpEvent, HttpHandler, HttpInterceptor, HttpRequest, HttpResponse } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { LaximoTokensStoreService } from '@services/utils/laximo-tokens-store.service';
import { Observable, tap } from 'rxjs';
import { LAXIMO_CATALOG_API_SEGMENT, LAXIMO_TOKENS_BALANCE_HEADER } from '../modules/catalog/laximo/models/laximo-tokens.constants';
import { isLaximoTokensExhaustedError } from '../modules/catalog/laximo/utils/laximo-tokens-error.util';

/**
 * Mirrors token charges of the Laximo catalog methods into `LaximoTokensStoreService`.
 * Charged methods are configured on the server, so every `laximo/catalogs/*` response is checked
 * by the presence of the balance header rather than by a client-side list of endpoints.
 */
@Injectable()
export class LaximoTokensInterceptor implements HttpInterceptor {
  private readonly tokensStore = inject(LaximoTokensStoreService);

  private applyBalanceHeader(event: HttpEvent<unknown>): void {
    if (!(event instanceof HttpResponse)) {
      return;
    }

    const balance = Number.parseFloat(event.headers.get(LAXIMO_TOKENS_BALANCE_HEADER) ?? '');

    if (Number.isFinite(balance)) {
      this.tokensStore.applyBalance(balance);
    }
  }

  intercept(request: HttpRequest<unknown>, next: HttpHandler): Observable<HttpEvent<unknown>> {
    if (!request.url.includes(LAXIMO_CATALOG_API_SEGMENT)) {
      return next.handle(request);
    }

    return next.handle(request).pipe(
      tap({
        next: event => this.applyBalanceHeader(event),
        error: (error: unknown) => {
          if (isLaximoTokensExhaustedError(error)) {
            this.tokensStore.markBlocked();
          }
        },
      }),
    );
  }
}
